import { useApolloClient, useQuery } from '@apollo/client';
import type { UseInventoryListsFetch } from '@openx/components-administration/lib/utils/context/InventoryListHookTypes.ts';
import type { InventoryListByIdAPI, InventoryListsListFilters } from '@openx/types/inventoryList.ts';
import { type CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetInventoryListsListDocument,
  type GetInventoryListsListQuery,
  type GetInventoryListsListQueryVariables,
} from '../../../../graphql/query/inventoryList/GetInventoryListsList.generated.tsx';
import { findLastPopulatedPage } from '../../../../utils/findLastPopulatedPage.ts';
import { handlePaginationUpdate } from '../../../../utils/handlePaginationUpdate.ts';
import { useLastPopulatedPageRedirect } from '../../../../utils/useLastPopulatedPageRedirect.ts';
import { useListCriteria } from '../../../../utils/useListCriteria.ts';
import { defaultCriteria } from './constants.ts';
import { adjustCriteriaFilters } from './utils/adjustCriteriaFilters.ts';

export const useInventoryListsFetch: UseInventoryListsFetch = () => {
  const apolloClient = useApolloClient();
  const { criteria, setCriteria } = useListCriteria('inventoryLists', defaultCriteria);
  const [resolvingLastPopulatedPage, setResolvingLastPopulatedPage] = useState(false);

  const queryCriteria = useMemo(() => adjustCriteriaFilters(criteria), [criteria]);

  const { data, loading, error } = useQuery(GetInventoryListsListDocument, {
    variables: queryCriteria,
    fetchPolicy: 'network-only',
  });

  const inventoryListData = data?.inventorylist;
  const inventoryListResponseLoaded = inventoryListData !== undefined;

  const requestedPage = criteria.pagination?.pageNumber ?? 1;
  const pageSize = criteria.pagination?.pageSize ?? defaultCriteria[CriteriaDimension.PAGINATION].pageSize;
  const expectedOffset = (requestedPage - 1) * pageSize;

  const responseMatchesRequestedPage =
    inventoryListResponseLoaded && (inventoryListData.offset == null || inventoryListData.offset === expectedOffset);

  const objectsLength = inventoryListData?.objects?.length ?? 0;
  const hasMore = inventoryListResponseLoaded ? (inventoryListData.has_more ?? false) : undefined;

  const requestedPageIsOutOfRange = !loading && requestedPage > 1 && objectsLength === 0 && hasMore === false;

  const resolveLastPopulatedPage = useCallback(
    (outOfRangePage: number, abortSignal?: AbortSignal) => {
      setResolvingLastPopulatedPage(true);

      return findLastPopulatedPage({
        requestedPage: outOfRangePage,
        abortSignal,

        isPagePopulated: async (candidatePage, signal) => {
          const probeOffset = (candidatePage - 1) * pageSize;

          const { data: probeData } = await apolloClient.query<
            GetInventoryListsListQuery,
            GetInventoryListsListQueryVariables
          >({
            query: GetInventoryListsListDocument,
            variables: {
              ...queryCriteria,
              limit: 1,
              offset: probeOffset,
            },
            fetchPolicy: 'no-cache',
            context: {
              fetchOptions: {
                signal,
              },
            },
          });

          return (probeData.inventorylist?.objects?.length ?? 0) > 0;
        },
      });
    },
    [apolloClient, pageSize, queryCriteria]
  );

  const redirectToLastPopulatedPage = useCallback(
    (lastPopulatedPage: number, outOfRangePage: number) => {
      setResolvingLastPopulatedPage(false);

      setCriteria(previousCriteria => {
        const previousPagination = previousCriteria.pagination ?? defaultCriteria[CriteriaDimension.PAGINATION];

        if (previousPagination.pageNumber !== outOfRangePage) {
          return previousCriteria;
        }

        return {
          ...previousCriteria,
          [CriteriaDimension.PAGINATION]: {
            ...previousPagination,
            pageNumber: lastPopulatedPage,
          },
        };
      });
    },
    [setCriteria]
  );

  const updatePaginationNormally = useCallback(() => {
    setResolvingLastPopulatedPage(false);

    if (!responseMatchesRequestedPage) {
      return;
    }

    handlePaginationUpdate({
      defaultPagination: defaultCriteria[CriteriaDimension.PAGINATION],
      hasMore,
      isLoading: loading,
      objectsLength,
      setCriteria,
    });
  }, [hasMore, loading, objectsLength, responseMatchesRequestedPage, setCriteria]);

  useEffect(() => {
    if (!responseMatchesRequestedPage || requestedPageIsOutOfRange) {
      return;
    }

    updatePaginationNormally();
  }, [requestedPageIsOutOfRange, responseMatchesRequestedPage, updatePaginationNormally]);

  useLastPopulatedPageRedirect({
    enabled: responseMatchesRequestedPage,
    requestedPage,
    loading,
    objectsLength,
    hasMore,
    resolveLastPopulatedPage,
    onRedirect: redirectToLastPopulatedPage,
    onFailure: updatePaginationNormally,
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, InventoryListsListFilters>) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual(newCriteriaProcessed, criteria)) {
        setCriteria(newCriteriaProcessed);
      }
    },
    [criteria, setCriteria]
  );

  const inventoryLists = useMemo<InventoryListByIdAPI[]>(
    () => inventoryListData?.objects ?? [],
    [inventoryListData?.objects]
  );

  return {
    loading: loading || resolvingLastPopulatedPage,
    inventoryLists,
    criteria,
    setCriteria: handleCriteriaChange,
    error,
  };
};
